import React, { useState, useEffect, useCallback } from 'react';
import './UpdatePlanModal.css';
import SwitchPlanModal from './SwitchPlanModal';
import PlanSwapTodayModal from './PlanSwapTodayModal';
import PlanWizard from './PlanWizard';
import PlanEditor from './PlanEditor';
import ModalLoading from './ModalLoading';
import { getDojoStrategyAccess } from '../utils/dojoStrategyAccess';

const UpdatePlanModal = ({ close, modalService, backendService, initialPlanId = null, focusEntryId = null }) => {
  const [planId, setPlanId] = useState(initialPlanId || '');
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [dojoName, setDojoName] = useState(null);
  const [authorityError, setAuthorityError] = useState(false);

  // ─── Resolve which plan to use ───
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const dojo = await backendService.getMyDojo();
        const [plansData, affiliationData] = await Promise.all([
          backendService.fetchPlans().catch(err => {
            console.error('[UpdatePlanModal] Failed to fetch plans:', err);
            return [];
          }),
          backendService.fetchUserAffiliation().catch(err => {
            console.error('[UpdatePlanModal] Error fetching user affiliation:', err);
            return null;
          })
        ]);

        setPlans(plansData);

        const access = getDojoStrategyAccess(dojo);
        const activityPlanId = affiliationData ? affiliationData.activity_plan_id : null;
        const nextPlanId = access.inDojo ? (dojo?.workout_plan_id || '') : (initialPlanId || activityPlanId || '');
        setPlanId(nextPlanId);
        setReadOnly(access.readOnly);
        setDojoName(access.dojoName);
        setAuthorityError(false);
      } catch (error) {
        console.error('[UpdatePlanModal] Initialization error:', error);
        setPlanId('');
        setReadOnly(true);
        setAuthorityError(true);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [backendService, initialPlanId]);

  const openDojoSettings = useCallback(async () => {
    const { default: DojoSettingsModal } = await import('./DojoSettingsModal');
    close?.();
    modalService.open(DojoSettingsModal, { backendService });
  }, [backendService, close, modalService]);

  const handleChoosePlan = async (selectedPlanId) => {
    // If switching to a different plan than the currently active one, ask the
    // user how to handle today's existing activities so they don't end up with
    // a doubled-up day.
    const isSwitchingPlan = !!planId && selectedPlanId && selectedPlanId !== planId;

    const applyAffiliation = async (todayStrategy) => {
      try {
        const success = await backendService.setPlanAffiliation(selectedPlanId, todayStrategy);
        if (success) {
          setPlanId(selectedPlanId);
        } else {
          console.error('[UpdatePlanModal] Failed to set plan affiliation');
        }
      } catch (error) {
        console.error('[UpdatePlanModal] Error setting plan affiliation:', error);
      }
    };

    if (isSwitchingPlan) {
      modalService.open(PlanSwapTodayModal, {
        onSelect: (strategy) => { applyAffiliation(strategy); },
      });
    } else {
      // First-time pick or re-selecting the same plan: behave as before.
      await applyAffiliation('add');
    }
  };

  const openSwitchPlanModal = () => {
    modalService.open(SwitchPlanModal, {
      backendService,
      modalService,
      currentPlanId: planId,
      onPlanSelected: handleChoosePlan,
      isUsingUnits: false
    });
  };

  const handleWizardPlanCreated = useCallback(async (newPlanId) => {
    setPlanId(newPlanId);
    setShowWizard(false);
    try {
      const plansData = await backendService.fetchPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('[UpdatePlanModal] Error loading new plan:', error);
    }
  }, [backendService]);

  if (isLoading) {
    return (
      <div className="update-plan-modal loading">
        <ModalLoading text="Loading plan data..." />
      </div>
    );
  }

  if (!readOnly && (showWizard || (!planId && plans.length === 0))) {
    return (
      <PlanWizard
        close={close}
        modalService={modalService}
        backendService={backendService}
        onPlanCreated={handleWizardPlanCreated}
        isOnboarding={false}
      />
    );
  }

  return (
    <div className="update-plan-modal">
      <div className="update-plan-header">
        <h2 className="update-plan-title">{readOnly ? 'Dojo Workout Plan' : 'My Workout Plan'}</h2>
        {readOnly && <div className="update-plan-readonly-notice">{authorityError ? '🔒 We could not verify plan ownership. Editing is disabled until dojo authority can be confirmed.' : <>🔒 Strategy and plan are established by {dojoName || 'your dojo'}. Only the dojo chief can change them. <button type="button" className="strategy-authority-link" onClick={openDojoSettings}>View dojo settings</button></>}</div>}
        <div className="update-plan-top-actions">
          {!readOnly && <button onClick={openSwitchPlanModal} className="btn-switch-plan">
            Switch plan
          </button>}
        </div>
        <div className="update-plan-subhead">
          Plan items define a repeating schedule. Expand an item to edit its details and see the generated workout.
        </div>
      </div>

      {!planId && readOnly && <div className="update-plan-readonly-notice">The dojo chief has not assigned a workout plan yet.</div>}

      {planId && <PlanEditor
        planId={planId}
        backendService={backendService}
        modalService={modalService}
        focusEntryId={focusEntryId}
        onClose={close}
        readOnly={readOnly}
        />}
    </div>
  );
};

export default UpdatePlanModal;